import { useTranslation } from "react-i18next";
import { useScrollAnimation } from "../../../hooks/useScrollAnimation";

const FEATURE_ICONS = [
  "ri-paint-brush-line",
  "ri-smartphone-line",
  "ri-search-line",
  "ri-speed-up-line",
  "ri-layout-line",
  "ri-shield-check-line",
  "ri-bug-line",
];

const TIMELINE_COUNT = 4;

export default function CustomWebsiteSection() {
  const { t } = useTranslation();
  const { ref: textRef, isVisible: textVis } = useScrollAnimation(0.1);
  const { ref: timeRef, isVisible: timeVis } = useScrollAnimation(0.1);

  return (
    <section id="custom-website" className="py-28 bg-[#F7F5F2]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Split layout */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-14 items-center mb-16">
          {/* Left — Image */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden w-full h-[420px] xl:h-[500px]">
              <img
                src="https://public.readdy.ai/ai/img_res/e62b590bcb0ed4131f556b606db59ec7.jpg"
                alt="Custom Website Design"
                className="w-full h-full object-top object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -right-5 bg-[#0C0C0C] text-white rounded-2xl px-6 py-4 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C8953A]">
                <i className="ri-award-line text-[#0C0C0C] text-xl" />
              </div>
              <div>
                <div className="text-xs text-white/40">{t("cw.badge.label")}</div>
                <div className="text-white font-bold text-sm">{t("cw.badge.value")}</div>
              </div>
            </div>
          </div>

          {/* Right — Text */}
          <div ref={textRef}>
            <span className={`text-[#C8953A] text-sm font-semibold tracking-widest uppercase ${textVis ? "anim-fade-right" : "opacity-0"}`}>
              {t("cw.eyebrow")}
            </span>
            <h2 className={`text-4xl xl:text-5xl font-bold text-[#0C0C0C] mt-3 mb-5 leading-tight ${textVis ? "anim-fade-right anim-d-100" : "opacity-0"}`}>
              {t("cw.titleL1")}
              <br />
              {t("cw.titleL2")}
            </h2>
            <p className={`text-[#0C0C0C]/55 text-base leading-relaxed mb-8 ${textVis ? "anim-fade-right anim-d-200" : "opacity-0"}`}>
              {t("cw.sub")}
            </p>
            <div className={`grid grid-cols-1 gap-3.5 ${textVis ? "anim-fade-right anim-d-300" : "opacity-0"}`}>
              {FEATURE_ICONS.map((icon, idx) => {
                const isKey = idx === 6;
                return (
                  <div key={idx} className="flex items-start gap-4 group">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors duration-200 ${isKey ? "bg-[#C8953A]/20 group-hover:bg-[#C8953A]/30" : "bg-[#C8953A]/10 group-hover:bg-[#C8953A]/20"}`}>
                      <i className={`${icon} text-[#C8953A] text-base`} />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-sm flex items-center gap-2 text-[#0C0C0C]">
                        {t(`cw.f${idx}.title`)}
                        {isKey && (
                          <span className="text-[10px] font-bold bg-[#C8953A] text-white px-2 py-0.5 rounded-full tracking-wide">
                            {t("cw.f6.badge")}
                          </span>
                        )}
                      </div>
                      <div className="text-[#0C0C0C]/45 text-xs mt-0.5 leading-relaxed">{t(`cw.f${idx}.desc`)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div ref={timeRef}>
          <div className={`text-center mb-12 ${timeVis ? "anim-fade-up" : "opacity-0"}`}>
            <h3 className="text-3xl font-bold text-[#0C0C0C]">{t("cw.tl.title")}</h3>
            <p className="text-[#0C0C0C]/45 text-sm mt-2">{t("cw.tl.sub")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {Array.from({ length: TIMELINE_COUNT }, (_, i) => (
              <div
                key={i}
                className={`relative bg-white rounded-2xl p-6 ${timeVis ? `anim-fade-up anim-d-${(i + 1) * 100}` : "opacity-0"}`}
              >
                <div className="text-6xl font-bold text-[#0C0C0C]/5 absolute top-4 right-5 leading-none select-none">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="inline-flex items-center gap-1.5 bg-[#C8953A]/10 rounded-full px-3 py-1 mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C8953A]" />
                  <span className="text-[#C8953A] text-xs font-semibold">{t(`cw.tl${i}.week`)}</span>
                </div>
                <h4 className="text-[#0C0C0C] font-bold text-lg mb-2">{t(`cw.tl${i}.phase`)}</h4>
                <p className="text-[#0C0C0C]/45 text-xs leading-relaxed">{t(`cw.tl${i}.desc`)}</p>
                {i < TIMELINE_COUNT - 1 && (
                  <div className="hidden xl:block absolute -right-2.5 top-1/2 -translate-y-1/2 w-5 h-0.5 bg-[#C8953A]/30 z-10" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
